import i18n from "@/plugins/i18n"
import store from "@/store"
import { slugify } from "./string"

/**
 * VALIDATORS FACTORIES
 */

export function mkComboboxSlug(validators) {
  return values => {
    let isValid = true

    // Check validator on each values
    for (const validator of validators) {
      for (const value of values) {
        isValid = validator(value)
        if (isValid !== true) return isValid
      }
    }

    // Check no duplicate slugged values
    const slugged = values.map(e => slugify(e))
    const hasDuplicate = new Set(slugged).size !== values.length
    if (hasDuplicate) {
      return i18n.t("messages.validator.no_duplicate_values")
    }

    return isValid
  }
}

/**
 * Make a string length validator. Check value's length is between limits
 * @param  {Number}     min   The minimum length
 * @param  {Number}     max   The maximum length
 * @return {Function}   The validating function
 */
function mkLength(min = 0, max = 10000) {
  return v => {
    const lgth = v?.length || 0
    return (
      (min <= lgth && lgth <= max) || i18n.t("messages.validator.length", { min: min, max: max })
    )
  }
}

/**
 * Make a "uniqueValue" validator providing the list of used values
 * @param  {Array}    values   List of values to check
 * @param  {String}   resource The name of the resource
 * @return {Function} The validating function
 */
export function mkUnique(values, resource) {
  const lowValues = values.map(e => (e ? slugify(e) : ""))
  return v => {
    return (
      (v ? !lowValues.includes(slugify(v)) : false) ||
      i18n.t("messages.validator.already_used", { resource: resource })
    )
  }
}

export function dateCompare(start_date, end_date) {
  const startDateObj = new Date(start_date)
  const endDateObj = new Date(end_date)
  if (startDateObj <= endDateObj) {
    return true
  } else {
    return i18n.t("messages.validator.date_compare")
  }
}

/**
 * RAW VALIDATORS
 */

/**
 * Email validator. Check value is a valid email.
 * @param  {String}         v   The value to check/validate
 * @return {Boolean|String}     True or error message
 */
export const email = v => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(String(v).toLowerCase()) || i18n.t("messages.validator.email_not_valid")
}

/**
 * Phone number validator. Check value is a valid phone number.
 * @param  {String}         v   The value to check/validate
 * @return {Boolean|String}     True or error message
 */
export const phone = v => {
  if (v === null || v === undefined || v === '') return true
  //const re = /^[+]?[0-9]{1,3}?[-.\s]?\(?[0-9]{1,4}?\)?[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,9}$/;
  const re = /^\+225[0-9]{10}$/;
  return re.test(String(v)) || i18n.t("messages.validator.phone_not_valid");
};

/**
 * Decimal validator. Check value is a valid decimal number with up to two decimal places.
 * @param  {String|Number}  v   The value to check/validate
 * @return {Boolean|String}     True or error message
 */
export const decimal = v => {
  // Regex to check if the value is a valid decimal number with up to two decimal places
  const re = /^[0-9]+(\.[0-9]{1,2})?$/
  // Return true if the value is valid, otherwise return an error message
  return re.test(String(v)) || i18n.t("messages.validator.decimal_not_valid")
}

/**
 * Image validator. Check value is a valid image file.
 * @param  {String}         v   The value to check/validate
 * @return {Boolean|String}     True or error message
 */
export function imageValidator(v) {
  let file = v
  let allowedTypes = ["image/jpeg", "image/png", "image/gif"]

  if (!allowedTypes.includes(file.type)) {
    store.commit("app/message", {
      msg: `${i18n.t("messages.error.invalid_file")}.
      ${i18n.t("messages.error.type_image_accept")}`,
      color: "error"
    })
    return
  }
  return true
}

export const max4 = mkLength(0, 4)
export const max8 = mkLength(0, 8)
export const max64 = mkLength(0, 64)
export const max1024 = mkLength(0, 1024)
export const max2048 = mkLength(0, 2048)

/**
 * Password validator. Check value is a valid password.
 * @param  {String}         v   The value to check/validate
 * @return {Boolean|String}     True or error message
 */
export const password = v => {
  const re = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/gm
  return re.test(v) || i18n.t("messages.validator.password")
}

/**
 * Path validator. Check value is a valid path.
 * @param  {String}         v   The value to check/validate
 * @return {Boolean|String}     True or error message
 */
export function path(v) {
  const re = /^[a-zA-Z0-9/_-]*$/gm
  return re.test(v) || i18n.t("messages.validator.regex")
}

/**
 * Required validator. Assert value is not empty, null or undefined.
 * @param  {String}         v   The value to check/validate
 * @return {Boolean|String}     True or error message
 */
export const required = v => {
  return (
    !(v === null || v === undefined || String(v).replace(" ", "") === "") ||
    i18n.t("messages.validator.field_required")
  )
}

/**
 * Slug validator. Check value is a slug.
 * @param  {String}         v   The value to check/validate
 * @return {Boolean|String}     True or error message
 */
export function slug(v) {
  const re = /^[a-zA-Z0-9_-]*$/gm
  return re.test(v) || i18n.t("messages.validator.regex")
}

/**
 * Number validator. Asserts that the value is a non-null, non-undefined number.
 * @param  {String}            v   The value to check/validate
 * @return {Boolean|String}     True or error message
 */
export const isNumber = v => {
  // Check if the value is not null, not undefined, and is a number
  // If value is empty, skip validation (not required)
  if (v === null || v === undefined || v === '') return true
  const regex = /^-?\d+(\.\d+)?$/gm
  return regex.test(v) || i18n.t("messages.validator.regex_number")
}

/**
 * Decimal validator. Asserts that the value is a non-null, non-undefined decimal number.
 * @param   {String}  v           La valeur à valider
 * @param   {Number}  [decimals]  Nombre fixe de décimales (si omis, n'importe quel nombre décimal est accepté)
 * @return  {Boolean|String}      True ou message d’erreur
 */
export const isDecimal = (v, decimals) => {
  // Valeur vide → on skip la validation (pas de required ici)
  if (v === null || v === undefined || v === '') return true

  let regex
  if (typeof decimals === 'number') {
    // Ex. /^-?\d+\.\d{2}$/ pour 2 décimales
    regex = new RegExp(`^-?\\d+\\.\\d{${decimals}}$`)
  } else {
    // Au moins une décimale
    regex = /^-?\d+\.\d+$/
  }

  return regex.test(v) || i18n.t(
    decimals != null
      ? "Ce champ ne doit contenir un nombre décimal"   // ex: "Doit être un nombre avec {{decimals}} décimales"
      : "Ce champ ne doit contenir un nombre décimal"         // ex: "Doit être un nombre décimal"
    ,
    { decimals }
  )
}

/**
 * Length validator. Check if the value's length does not exceed 255 characters.
 * @param  {String}         v   The value to check/validate
 * @return {Boolean|String}     True or error message
 */
export function validateLength(v) {
  return v.length <= 255 || i18n.t("messages.validator.lengthword")
}
